import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import FileUploader from "@/components/FileUploader";
import { Plus, Pencil, Trash2 } from "lucide-react";

const EMPTY = {
  name: "", slug: "", short: "", hero_image: "", overview: "", market_summary: "", connectivity: "",
  employment_hubs: "", schools: "", hospitals: "", price_range: "", investment: "", faqs_text: "", published: true,
};
const toArr = (s) => (s || "").split(",").map((x) => x.trim()).filter(Boolean);
const toStr = (a) => (Array.isArray(a) ? a.join(", ") : a || "");
const faqsToText = (faqs) => (faqs || []).map((f) => `${f.q} | ${f.a}`).join("\n");
const textToFaqs = (t) => (t || "").split("\n").map((l) => l.trim()).filter(Boolean).map((l) => { const [q, ...a] = l.split("|"); return { q: (q || "").trim(), a: a.join("|").trim() }; }).filter((f) => f.q);

export default function AdminLocations() {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/admin/locations").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const openEdit = (l) => {
    setEditing(l);
    setForm({ ...EMPTY, ...l, employment_hubs: toStr(l.employment_hubs), schools: toStr(l.schools), hospitals: toStr(l.hospitals), investment: toStr(l.investment), faqs_text: faqsToText(l.faqs) });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name) { toast.error("Name is required"); return; }
    const payload = { ...form, employment_hubs: toArr(form.employment_hubs), schools: toArr(form.schools), hospitals: toArr(form.hospitals), investment: toArr(form.investment), faqs: textToFaqs(form.faqs_text) };
    delete payload.faqs_text;
    try {
      if (editing) await api.put(`/admin/locations/${editing.id}`, payload);
      else await api.post("/admin/locations", payload);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (l) => { if (!window.confirm(`Delete "${l.name}"?`)) return; await api.delete(`/admin/locations/${l.id}`); toast.success("Deleted"); load(); };

  return (
    <div className="p-6 md:p-10">
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="font-display text-3xl text-navy">Locations</h1><p className="text-muted-foreground mt-1">Manage location landing pages (/properties-in-slug).</p></div>
        <Button onClick={() => { setEditing(null); setForm(EMPTY); setOpen(true); }} data-testid="add-location-btn" className="bg-gold text-navy hover:bg-gold-dark font-semibold"><Plus className="w-4 h-4 mr-1" /> Add Location</Button>
      </div>
      {items === null ? <Skeleton className="h-64 rounded-2xl" /> : (
        <div className="grid md:grid-cols-2 gap-4">
          {items.map((l) => (
            <div key={l.id} className="bg-white rounded-2xl border border-gray-100 shadow-card p-4 flex items-center gap-4" data-testid={`location-admin-${l.id}`}>
              {l.hero_image ? <img src={l.hero_image} alt="" className="w-16 h-16 rounded-lg object-cover" /> : <div className="w-16 h-16 rounded-lg bg-muted" />}
              <div className="flex-1"><p className="font-semibold text-navy">{l.name}</p><p className="text-xs text-muted-foreground">/properties-in-{l.slug}</p><span className={`text-xs mt-1 inline-block px-2 py-0.5 rounded-full ${l.published ? "bg-emerald-50 text-emerald-700" : "bg-gray-100 text-gray-500"}`}>{l.published ? "Published" : "Draft"}</span></div>
              <Button size="icon" variant="ghost" onClick={() => openEdit(l)}><Pencil className="w-4 h-4" /></Button>
              <Button size="icon" variant="ghost" onClick={() => del(l)} className="text-red-500"><Trash2 className="w-4 h-4" /></Button>
            </div>
          ))}
          {items.length === 0 && <p className="text-center text-muted-foreground py-10 md:col-span-2">No locations yet.</p>}
        </div>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-navy">{editing ? "Edit" : "Add"} Location</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-4">
            <F label="Name *" v={form.name} on={(v) => set("name", v)} />
            <F label="Short Name" v={form.short} on={(v) => set("short", v)} />
          </div>
          <FileUploader mode="image" value={form.hero_image} onChange={(v) => set("hero_image", v)} label="Hero Image" />
          <A label="Overview" v={form.overview} on={(v) => set("overview", v)} />
          <A label="Market Summary" v={form.market_summary} on={(v) => set("market_summary", v)} />
          <A label="Connectivity" v={form.connectivity} on={(v) => set("connectivity", v)} />
          <F label="Employment Hubs (comma separated)" v={form.employment_hubs} on={(v) => set("employment_hubs", v)} />
          <F label="Schools (comma separated)" v={form.schools} on={(v) => set("schools", v)} />
          <F label="Hospitals (comma separated)" v={form.hospitals} on={(v) => set("hospitals", v)} />
          <F label="Price Range" v={form.price_range} on={(v) => set("price_range", v)} />
          <F label="Investment Advantages (comma separated)" v={form.investment} on={(v) => set("investment", v)} />
          <A label="FAQs (one per line, format: Question | Answer)" v={form.faqs_text} on={(v) => set("faqs_text", v)} />
          <div className="flex items-center gap-2"><Switch checked={form.published} onCheckedChange={(v) => set("published", v)} /><Label className="text-sm">Published</Label></div>
          <Button onClick={save} data-testid="save-location-btn" className="bg-gold text-navy hover:bg-gold-dark font-semibold">Save Location</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const F = ({ label, v, on }) => (<div><Label className="text-xs font-semibold text-navy">{label}</Label><Input value={v || ""} onChange={(e) => on(e.target.value)} className="bg-gray-50 mt-1" /></div>);
const A = ({ label, v, on }) => (<div><Label className="text-xs font-semibold text-navy">{label}</Label><Textarea value={v || ""} onChange={(e) => on(e.target.value)} className="bg-gray-50 mt-1" rows={2} /></div>);
